import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Server, Database, Code, HeadphonesIcon, Settings, Users } from 'lucide-react';

export function SolutionsPage() {
  const solutions = [
    {
      icon: Server,
      title: 'Nestor Implementation',
      description: 'End-to-end implementation with project management, training, and go-live support',
      features: ['Requirement Analysis', 'System Configuration', 'Data Migration', 'Go-Live Support']
    },
    {
      icon: Database,
      title: 'Data Migration',
      description: 'Secure migration from legacy systems with zero data loss and minimal downtime',
      features: ['Data Mapping', 'ETL Processes', 'Validation', 'Rollback Plan']
    },
    {
      icon: Code,
      title: 'Custom Integrations & APIs',
      description: 'Connect with your existing tools and systems through powerful APIs',
      features: ['REST APIs', 'Webhooks', 'SSO Integration', 'Custom Development']
    },
    {
      icon: HeadphonesIcon,
      title: 'Ongoing Support & SLA',
      description: '24/7 dedicated support with guaranteed response times and SLAs',
      features: ['24/7 Support', '99.9% Uptime SLA', 'Dedicated Account Manager', 'Priority Resolution']
    },
    {
      icon: Settings,
      title: 'Payroll Setup & Compliance',
      description: 'Complete payroll configuration with statutory compliance for your region',
      features: ['Payroll Configuration', 'Tax Setup', 'Compliance Support', 'Regular Updates']
    },
    {
      icon: Users,
      title: 'HR Process Consulting',
      description: 'Expert guidance to optimize your HR processes and workflows',
      features: ['Process Assessment', 'Best Practices', 'Workflow Design', 'Change Management']
    },
  ];

  return (
    <>
      <SEO
        title="Solutions & Services - Nestor"
        description="Professional services for Nestor implementation, data migration, custom integrations, and ongoing support. Expert guidance for your HR transformation."
      />

      <section className="bg-gradient-to-b from-primary/10 via-background to-background py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Solutions & Services</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Comprehensive professional services to ensure your Nestor success from day one
            </p>
            <Button size="lg" asChild>
              <Link to="/contact">Talk to Sales <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {solutions.map((solution, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <solution.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>{solution.title}</CardTitle>
                  <CardDescription>{solution.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/40">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let's discuss your requirements and create a custom solution for your organization.
            </p>
            <Button size="lg" asChild>
              <Link to="/contact">Contact Sales <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
